import { Input, Select } from '@baseV2'
import { showDateFormat, displayAmount, formatMixedQuantities } from '@utils'
import { strings } from '@constants'

import {
	DetailGroup,
	SectionDivider,
	DetailRow,
	DetailLabel,
	DetailValue,
	TrackingLinkRow,
	TrackingLinkValue,
	FieldRight,
	FieldError,
	EditControlWrapper,
} from './style'
import { Calendar } from '@base'
import { OpenInNewIcon } from '@pages/Admin/Content/Banner/styles'

export const DELIVERY_STATUS_OPTIONS = [
	{ status: 'LOADED', label: 'Loaded' },
	{ status: 'EN ROUTE', label: 'En Route' },
	{ status: 'DELIVERED', label: 'Delivered' },
	{ status: 'SALES_RETURN', label: 'Sales Return' },
]

const DELIVERY_ONLY_KEYS = ['delivery_status', 'delivery_date']

const BasicDetailsForm = ({ invoiceData, formState, setField, errors, isSaving, isEditMode, supplierPoNumber, deliveryOnly = false }) => {
	const invoiceQuantityText = invoiceData?.order_items?.length
		? formatMixedQuantities(invoiceData.order_items, 'serviced_weight')
		: invoiceData?.invoice_quantity
		? `${invoiceData.invoice_quantity} MT`
		: '-'

	const vendorBillList = invoiceData?.vendor_bill_no ?? []
	const hasVendorBill = vendorBillList.length > 0
	const vendorBillText = hasVendorBill ? vendorBillList.join(', ') : '-'
	const vendorBillEditable = !supplierPoNumber

	const renderRow = ({ key, label, viewValue, editControl }) => {
		const showEdit = isEditMode && (!deliveryOnly || DELIVERY_ONLY_KEYS.includes(key))
		return (
			<DetailRow key={key} $editMode={isEditMode}>
				<DetailLabel>{label}</DetailLabel>
				<FieldRight>{showEdit ? <EditControlWrapper>{editControl}</EditControlWrapper> : viewValue}</FieldRight>
			</DetailRow>
		)
	}

	const group1 = [
		{
			key: 'original_invoice_number',
			label: strings('invoice', 'number'),
			viewValue: <DetailValue>{invoiceData?.original_invoice_number || invoiceData?.invoice_number || '-'}</DetailValue>,
			editControl: (
				<Input
					type='text'
					small
					value={formState.original_invoice_number ?? ''}
					onChange={(e) => setField('original_invoice_number', e.target.value)}
					disabled={isSaving}
					error={errors.original_invoice_number}
				/>
			),
		},
		{
			key: 'invoice_date',
			label: strings('invoice', 'date'),
			viewValue: <DetailValue>{invoiceData?.invoice_date ? showDateFormat(new Date(invoiceData.invoice_date)) : '-'}</DetailValue>,
			editControl: (
				<>
					<Calendar value={formState.invoice_date} onChange={(v) => setField('invoice_date', v)} disabled={isSaving} />
					{errors.invoice_date && <FieldError>{errors.invoice_date}</FieldError>}
				</>
			),
		},
		{
			key: 'invoice_amount',
			label: strings('invoice', 'amount'),
			viewValue: <DetailValue>{invoiceData?.invoice_amount ? displayAmount(invoiceData.invoice_amount) : '-'}</DetailValue>,
			editControl: (
				<Input
					type='number'
					min='0'
					leftIcon='₹'
					value={formState.invoice_amount ?? ''}
					onChange={(e) => setField('invoice_amount', e.target.value)}
					disabled={isSaving}
					error={errors.invoice_amount}
				/>
			),
		},
		{
			key: 'invoice_quantity',
			label: 'Invoice Quantity',
			viewValue: <DetailValue>{invoiceQuantityText}</DetailValue>,
			editControl: <DetailValue>{invoiceQuantityText}</DetailValue>,
		},
	]

	const group2 = [
		{
			key: 'eway_bill_no',
			label: strings('eway_bill_no'),
			viewValue: <DetailValue>{invoiceData?.eway_details?.e_way_bill_no || '-'}</DetailValue>,
			editControl: <Input type='text' small value={formState.eway_bill_no ?? ''} disabled={true} error={errors.eway_bill_no} />,
		},
		{
			key: 'eway_expiry',
			label: strings('eway_bill_valid_till'),
			viewValue: (
				<DetailValue>
					{invoiceData?.eway_details?.valid_till ? showDateFormat(new Date(invoiceData.eway_details.valid_till)) : '-'}
				</DetailValue>
			),
			editControl: (
				<>
					<Calendar value={invoiceData?.eway_details?.valid_till} disabled={true} />
				</>
			),
		},
		{
			key: 'vendor_bill_no',
			label: strings('vendor_bill_no'),
			viewValue: <DetailValue>{vendorBillText}</DetailValue>,
			// Edit mode itself is super-admin only.
			editControl: !vendorBillEditable ? (
				<DetailValue>{vendorBillText}</DetailValue>
			) : (
				<Input
					type='text'
					small
					value={formState.vendor_bill_no ?? ''}
					onChange={(e) => setField('vendor_bill_no', e.target.value)}
					disabled={isSaving}
					placeholder={strings('vendor_bill_no')}
					error={errors.vendor_bill_no}
				/>
			),
		},
		{
			key: 'delivery_status',
			label: strings('delivery', 'status'),
			viewValue: <DetailValue>{invoiceData?.delivery_status || '-'}</DetailValue>,
			editControl: (
				<Select
					small
					data={DELIVERY_STATUS_OPTIONS}
					displayKey='label'
					primaryKey='status'
					value={formState.delivery_status}
					onChange={(v) => setField('delivery_status', v)}
					disabled={isSaving}
					error={errors.delivery_status}
				/>
			),
		},
		{
			key: 'delivery_date',
			label: strings('delivery_date'),
			viewValue: <DetailValue>{invoiceData?.delivery_date ? showDateFormat(new Date(invoiceData.delivery_date)) : '-'}</DetailValue>,
			editControl: (
				<>
					<Calendar value={formState.delivery_date} onChange={(v) => setField('delivery_date', v)} disabled={isSaving} />
					{errors.delivery_date && <FieldError>{errors.delivery_date}</FieldError>}
				</>
			),
		},
		{
			key: 'gps_link',
			label: strings('tracking_link'),
			viewValue: invoiceData?.gps_link ? (
				<TrackingLinkRow>
					<TrackingLinkValue href={invoiceData.gps_link} target='_blank' rel='noopener noreferrer'>
						{strings('view')}
					</TrackingLinkValue>
					<OpenInNewIcon
						fontSize='small'
						onClick={(e) => {
							e.stopPropagation()
							invoiceData?.gps_link && window.open(invoiceData?.gps_link, '_blank')
						}}
					/>
				</TrackingLinkRow>
			) : (
				<DetailValue>-</DetailValue>
			),
			editControl: (
				<Input
					type='url'
					small
					value={formState.gps_link ?? ''}
					onChange={(e) => setField('gps_link', e.target.value)}
					disabled={isSaving}
					placeholder='https://'
					error={errors.gps_link}
				/>
			),
		},
	]

	return (
		<DetailGroup>
			{group1.map((field) => renderRow(field))}
			<SectionDivider />
			{group2.map((field) => renderRow(field))}
		</DetailGroup>
	)
}

export default BasicDetailsForm
